import { GetProjectResponse } from "api/generatedApi";
import { Role, useRoleRestriction } from "./use-role-restriction";

export const useDashboardRestrictions = (project: GetProjectResponse) => {
  const { hasRole } = useRoleRestriction(project);

  // Favorites
  const canCreateFavorites = (): boolean => {
    return hasRole([Role.Manager, Role.AssistantOwner, Role.Owner]);
  };
  const canSelectFavorites = (): boolean => {
    return hasRole([Role.Manager, Role.AssistantOwner, Role.Owner]);
  };
  const canDeleteFavorites = (selectedItems: Set<string>): boolean => {
    if (!selectedItems || selectedItems.size <= 0) {
      return false;
    }
    return hasRole([Role.Manager, Role.AssistantOwner, Role.Owner]);
  };

  // Extra Agreements
  const canViewExtraAgreementsRates = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager, Role.Participant]);
  };
  const canEditExtraAgreementsRates = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager]);
  };
  const canCreateExtraWorkAgreements = (): boolean => {
    return hasRole([Role.Manager, Role.AssistantOwner, Role.Owner]);
  };
  const canSelectExtraWorkAgreements = (): boolean => {
    return true;
  };
  const canEditExtraWorkAgreement = (): boolean => {
    return hasRole([Role.Manager, Role.AssistantOwner, Role.Owner]);
  };
  const canDeleteExtraWorkAgreements = (selectedItems: Set<string>): boolean => {
    if (!selectedItems || selectedItems.size <= 0) {
      return false;
    }
    return hasRole([Role.Manager, Role.AssistantOwner, Role.Owner]);
  };

  // Agreed Lumpsum
  const canEditAgreedLumpsum = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  // Project setup
  const canEditProjectSetupProjectType = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager]);
  };
  const canEditProjectSetupProjectName = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager]);
  };
  const canEditProjectSetupProjectCompany = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager]);
  };
  const canCreateProjectUsers = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager]);
  };

  // Compensation payment
  const canViewCompensationPayment = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager]);
  };
  const canCreateCompensationPayment = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };
  const canSelectCompensationPayments = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };
  const canDeleteCompensationPayments = (selectedItems: Set<string>): boolean => {
    if (!selectedItems || selectedItems.size <= 0) {
      return false;
    }
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  // Projects Specific Operation
  const canCreateProjectSpecificOperation = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };
  const canSelectProjectSpecificOperations = (): Boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };
  const canEditProjectSpecificOperation = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };
  const canDeleteProjectSpecificOperations = (selectedItems: Set<string>): boolean => {
    if (!selectedItems || selectedItems.size <= 0) {
      return false;
    }
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  // Users
  const canViewProjectUsers = () => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager, Role.Participant]);
  };

  const canViewChangeProjectOwnerButton = () => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  // Reports
  const canDownloadProjectReport = () => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager, Role.Participant]);
  };
  const canDownloadStatusReport = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner, Role.Manager]);
  };

  // Calculations
  const canUploadCalculation = (): boolean => {
    return hasRole([Role.Owner, Role.AssistantOwner]);
  };

  return {
    canCreateFavorites,
    canSelectFavorites,
    canDeleteFavorites,
    canCreateExtraWorkAgreements,
    canSelectExtraWorkAgreements,
    canEditExtraWorkAgreement,
    canDeleteExtraWorkAgreements,
    canViewExtraAgreementsRates,
    canEditExtraAgreementsRates,
    canEditAgreedLumpsum,
    canEditProjectSetupProjectType,
    canEditProjectSetupProjectName,
    canEditProjectSetupProjectCompany,
    canCreateProjectUsers,
    canViewCompensationPayment,
    canCreateCompensationPayment,
    canSelectCompensationPayments,
    canDeleteCompensationPayments,
    canCreateProjectSpecificOperation,
    canSelectProjectSpecificOperations,
    canEditProjectSpecificOperation,
    canDeleteProjectSpecificOperations,
    canViewProjectUsers,
    canDownloadProjectReport,
    canDownloadStatusReport,
    canUploadCalculation,
    canViewChangeProjectOwnerButton,
  };
};
